import { addNotification, addConnectionRequestNotification } from '../state/notificationSlice';
import { store } from '../state';

// Configuration
const MAX_RETRY_ATTEMPTS = 3;
const CONNECTION_TIMEOUT = 10000; // 10 seconds
const RECONNECT_DELAY = 3000; // 3 seconds
const PING_INTERVAL = 30000; // 30 seconds for ping/pong
const NOTIFICATIONS_ENABLED = false; // Set to false to disable notifications feature

// State variables
let isSocketConnected = false;
let connectionTimeoutId = null;
let socketRetries = 0;
let pingIntervalId = null;

// Socket instance will be stored globally for access across components
window.socketInstance = null;

// Track recent notifications to prevent duplicates
window.recentNotifications = {};
window.maxRecentNotifications = 50;

/**
 * Checks if a notification has been processed recently to prevent duplicates
 * @param {string} notificationId - The ID of the notification
 * @param {string} type - The type of notification
 * @returns {boolean} - True if the notification is a duplicate
 */
function isDuplicateNotification(notificationId, type, timestamp) {
  if (!notificationId) return false;
  
  // Create a unique key based on ID and type
  const key = `${type || 'NOTIFICATION'}-${notificationId}`;
  
  // Check if we've seen this notification recently
  if (window.recentNotifications[key]) {
    const storedTime = window.recentNotifications[key];
    const newTime = timestamp ? new Date(timestamp).getTime() : Date.now();
    
    // If the new notification was received within 10 seconds of the previous one, consider it a duplicate
    if (Math.abs(newTime - storedTime) < 10000) {
      console.log('[Socket] Duplicate notification detected:', key);
      return true;
    }
  }
  
  // Store this notification timestamp
  window.recentNotifications[key] = timestamp ? new Date(timestamp).getTime() : Date.now();
  
  // Maintain max size for recent notifications
  const keys = Object.keys(window.recentNotifications);
  if (keys.length > window.maxRecentNotifications) {
    // Remove oldest entries
    const keysToRemove = keys.length - window.maxRecentNotifications;
    for (let i = 0; i < keysToRemove; i++) {
      delete window.recentNotifications[keys[i]];
    }
  }
  
  return false;
}

/**
 * Set up WebSocket connection for real-time notifications
 * @param {Object} store - Redux store for dispatching events
 * @returns {Function} - Cleanup function to disconnect socket
 */
const setupSockets = (store) => {
  // Skip socket setup if notifications are disabled
  if (!NOTIFICATIONS_ENABLED) {
    console.log('[Socket] Notifications are disabled, skipping socket setup');
    return () => {};
  }
  
  if (!store) {
    console.error('[Socket] No Redux store provided');
    return () => {};
  }
  
  // Get user ID and token from Redux state
  const state = store.getState();
  const userId = state?.auth?.user?.id;
  const token = state?.auth?.token;
  
  // Make store available globally for notification handlers
  window.store = store;
  
  if (!userId) {
    console.warn('[Socket] No user ID found, skipping socket setup');
    return () => {};
  }
  
  console.log('[Socket] Setting up WebSocket with user ID:', userId);
  
  // Connect to socket server after a small delay to allow app to initialize
  setTimeout(() => {
    connectWebSocket(userId, token, store);
  }, 500);
  
  // Return cleanup function
  return () => {
    console.log('[Socket] Cleaning up socket connection');
    
    // Clear timeout if it exists
    if (connectionTimeoutId) {
      clearTimeout(connectionTimeoutId);
      connectionTimeoutId = null;
    }
    
    // Clear ping interval
    if (pingIntervalId) {
      clearInterval(pingIntervalId);
      pingIntervalId = null;
    }
    
    // Disconnect socket if it exists
    if (window.socketInstance) {
      try {
        window.socketInstance.close();
        window.socketInstance = null;
      } catch (error) {
        console.error('[Socket] Error disconnecting socket:', error);
      }
    }
    
    // Reset state
    isSocketConnected = false;
    socketRetries = 0;
  };
};

/**
 * Connect to the WebSocket server
 * @param {string} userId - User ID for authentication
 * @param {string} token - Authentication token
 * @param {Object} store - Redux store for dispatching events
 */
function connectWebSocket(userId, token, store) {
  if (!NOTIFICATIONS_ENABLED) {
    console.log('[Socket] Notifications are disabled, skipping websocket connection');
    return;
  }
  
  // Bail if we don't have required info
  if (!userId) {
    console.warn('[Socket] Cannot connect socket: No user ID provided');
    return;
  }
  
  // Clean up existing socket if it exists
  if (window.socketInstance) {
    try {
      window.socketInstance.close();
      window.socketInstance = null;
    } catch (error) {
      console.error('[Socket] Error cleaning up existing socket:', error);
    }
  }
    
  // Clear existing timeout
  if (connectionTimeoutId) {
    clearTimeout(connectionTimeoutId);
  }
  
  // Clear existing ping interval
  if (pingIntervalId) {
    clearInterval(pingIntervalId);
    pingIntervalId = null;
  }
    
  // Set timeout to detect connection failures
  connectionTimeoutId = setTimeout(() => {
    if (!isSocketConnected) {
      console.warn('[Socket] Connection timed out, retrying...');
      socketRetries++;
      
      if (socketRetries < MAX_RETRY_ATTEMPTS) {
        console.log(`[Socket] Retry attempt ${socketRetries} of ${MAX_RETRY_ATTEMPTS}`);
        connectWebSocket(userId, token, store);
      } else {
        console.error('[Socket] Max retry attempts reached, giving up');
      }
    }
  }, CONNECTION_TIMEOUT);
    
  // Get WebSocket server URL
  const wsApiUrl = process.env.REACT_APP_WEBSOCKET_API_URL || 'ws://localhost:5007';
  
  try {
    console.log(`[Socket] Connecting to ${wsApiUrl} with user ID ${userId}`);
    
    // Create WebSocket instance with query parameters
    const socketUrl = `${wsApiUrl}?userId=${userId}&token=${encodeURIComponent(token)}&timestamp=${Date.now()}`;
    const socket = new WebSocket(socketUrl);
    
    // Store socket globally
    window.socketInstance = socket;
      
    // Set up connection event handlers
    socket.onopen = (event) => {
      console.log('[Socket] Connected successfully');
      isSocketConnected = true;
      socketRetries = 0;
        
      // Clear connection timeout
      if (connectionTimeoutId) {
        clearTimeout(connectionTimeoutId);
        connectionTimeoutId = null;
      }
        
      // Dispatch global event for components to listen to
      window.dispatchEvent(new CustomEvent('socket-connected', { 
        detail: { userId, socketId: 'websocket' }
      }));
      
      // Send authentication message
      const authMessage = {
        action: 'authenticate',
        userId: userId,
        token: token
      };
      
      socket.send(JSON.stringify(authMessage));
      
      // Set up ping interval to keep connection alive
      pingIntervalId = setInterval(() => {
        if (socket.readyState === WebSocket.OPEN) {
          try {
            socket.send(JSON.stringify({ action: 'ping', timestamp: Date.now() }));
          } catch (err) {
            console.error('[Socket] Error sending ping:', err);
          }
        }
      }, PING_INTERVAL);
    };
    
    socket.onclose = (event) => {
      console.log('[Socket] Disconnected:', event.code, event.reason);
      isSocketConnected = false;
      
      // Clear ping interval
      if (pingIntervalId) {
        clearInterval(pingIntervalId);
        pingIntervalId = null;
      }
      
      // Dispatch global event for components to listen to
      window.dispatchEvent(new CustomEvent('socket-disconnected', {
        detail: { code: event.code, reason: event.reason }
      }));
      
      // Try to reconnect with a delay if not a normal closure
      if (event.code !== 1000 && event.code !== 1001) {
        setTimeout(() => {
          console.log('[Socket] Attempting to reconnect...');
          connectWebSocket(userId, token, store);
        }, RECONNECT_DELAY);
      }
    };
    
    socket.onerror = (error) => {
      console.error('[Socket] Connection error:', error);
    };
    
    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('[Socket] Message received:', data);
        processSocketMessage(data, store);
      } catch (error) {
        console.error('[Socket] Error processing message:', error, event.data);
      }
    };
  } catch (error) {
    console.error('[Socket] Error creating socket connection:', error);
    socketRetries++;
    
    if (socketRetries < MAX_RETRY_ATTEMPTS) {
      console.log(`[Socket] Retrying in ${RECONNECT_DELAY}ms...`);
      setTimeout(() => {
        connectWebSocket(userId, token, store);
      }, RECONNECT_DELAY);
    }
  }
}

/**
 * Process messages from the WebSocket server
 * @param {Object} data - Parsed message data
 * @param {Object} store - Redux store for dispatching events
 */
function processSocketMessage(data, store) {
  if (!data || typeof data !== 'object') {
    console.warn('[Socket] Received invalid message format:', data);
    return;
  }

  // Determine the type of message from either type or event field
  const messageType = data.type || data.event || data.action;
  
  switch (messageType) {
    case 'notification':
      // Handle notification
      console.log('[Socket] Received notification:', data);
      
      // Dispatch to Redux store
      if (store && store.dispatch) {
        store.dispatch({
          type: 'NOTIFICATION_RECEIVED',
          payload: data.notification || data.data
        });
        
        // Notify components
        window.dispatchEvent(new CustomEvent('notification-received', {
          detail: data.notification || data.data
        }));
      }
      break;
    
    case 'auth_response':
      // Handle authentication response
      console.log('[Socket] Authentication response:', data.success ? 'Success' : 'Failed');
      
      if (data.success) {
        // Authentication was successful
        window.dispatchEvent(new CustomEvent('socket-authenticated', { 
          detail: { userId: data.userId }
        }));
        
        // Request notifications after successful authentication
        if (window.socketInstance && window.socketInstance.readyState === WebSocket.OPEN) {
          try {
            window.socketInstance.send(JSON.stringify({
              action: 'fetch_notifications',
              userId: data.userId
            }));
          } catch (err) {
            console.error('[Socket] Failed to request notifications:', err);
          }
        }
      } else {
        // Authentication failed
        console.error('[Socket] Authentication failed:', data.message || 'Unknown error');
      }
      break;
    
    case 'pong':
      // Handle pong response (keep-alive)
      console.log('[Socket] Received pong from server');
      break;
    
    case 'error':
      // Handle error messages
      console.error('[Socket] Server error:', data.message || 'Unknown error');
      break;
    
    case 'connection_status':
      // Handle connection status messages
      console.log('[Socket] Connection status update:', data.status);
      break;
    
    default:
      // Handle action-based messages
      if (data.action === 'fetch_notifications_response') {
        // Handle notifications list response
        console.log('[Socket] Received notifications list');
        
        if (store && store.dispatch && Array.isArray(data.notifications)) {
          store.dispatch({
            type: 'NOTIFICATIONS_LOADED',
            payload: data.notifications
          });
          
          window.dispatchEvent(new CustomEvent('notifications-loaded', {
            detail: { notifications: data.notifications }
          }));
        }
      } else if (messageType === 'connection_request') {
        // Handle connection request specifically
        handleConnectionRequest(data, store);
      } else if (messageType === 'notification_single') {
        // Handle individual notification
        handleSingleNotification(data, store);
      } else if (messageType === 'calendar_share_request') {
        // Handle calendar share request
        handleCalendarShareRequest(data, store);
      } else {
        // Unknown message type
        console.log('[Socket] Received unknown message type:', messageType, data);
      }
  }
}

/**
 * Handle connection request message
 * @param {Object} data - Connection request data
 * @param {Object} store - Redux store
 */
function handleConnectionRequest(data, store) {
  console.log('[Socket] Received connection_request event:', data);
  
  // Generate a unique request ID if none exists
  if (!data.id) {
    data.id = `req-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    console.log('[Socket] Generated ID for connection request:', data.id);
  }
  
  // To prevent duplicate events for the same request,
  // add a timestamp property if it doesn't exist
  if (!data.timestamp) {
    data.timestamp = new Date().toISOString();
  }
  
  // Check if we've handled this connection request recently
  if (window.lastConnectionRequestId === data.id) {
    console.log('[Socket] Duplicate connection request detected, not dispatching:', data.id);
    return;
  }
  
  // Store this request ID to prevent duplicates
  window.lastConnectionRequestId = data.id;
  
  // Clear the stored ID after a delay to allow future requests with same ID
  setTimeout(() => {
    if (window.lastConnectionRequestId === data.id) {
      window.lastConnectionRequestId = null;
    }
  }, 10000); // 10 seconds
  
  // Use specialized reducer for connection requests
  const currentStore = window.store || store;
  if (currentStore) {
    currentStore.dispatch(addConnectionRequestNotification(data));
    
    // Also dispatch to window event
    window.dispatchEvent(new CustomEvent('socket-connection-request', {
      detail: data
    }));
    
    // Update notification count
    window.dispatchEvent(new CustomEvent('notification-count-updated', {
      detail: { count: 1 }
    }));
  }
}

/**
 * Handle single notification message
 * @param {Object} data - Notification data
 * @param {Object} store - Redux store
 */
function handleSingleNotification(data, store) {
  try {
    console.log('[Socket] Received notification_single event:', data);
    
    if (!data) {
      console.warn('[Socket] Received empty notification data');
      return;
    }
    
    // Ensure we have an ID
    if (!data.id) {
      console.warn('[Socket] Notification missing ID, generating one');
      data.id = `notification-${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    }
    
    // Check if this is a duplicate notification we've processed recently
    if (isDuplicateNotification(data.id, data.type, data.timestamp)) {
      console.log('[Socket] Skipping duplicate notification:', data.id);
      return;
    }
    
    // Enhanced message extraction logic - prioritize direct message field
    let message = null;
    
    // Handle type-specific message formatting
    if (data.type === 'CONNECTION_REQUEST' || 
        data.type?.toUpperCase() === 'CONNECTION_REQUEST') {
      
      // Enhanced sender name extraction with multiple fallbacks
      let senderName = '';
      let senderId = null;
      
      // Extract sender from all possible locations
      if (data.sender && data.sender.name) {
        senderName = data.sender.name;
        senderId = data.sender.id;
      } else if (data.data && data.data.senderName) {
        senderName = data.data.senderName;
        senderId = data.data.senderId;
      } else if (data.senderName) {
        senderName = data.senderName;
        senderId = data.senderId;
      } else if (data.data && data.data.sender && data.data.sender.name) {
        senderName = data.data.sender.name;
        senderId = data.data.sender.id;
      } else if (data.data && data.data.requester && data.data.requester.name) {
        senderName = data.data.requester.name;
        senderId = data.data.requester.id;
      } else if (data.data && data.data.requesterName) {
        senderName = data.data.requesterName;
        senderId = data.data.requesterId;
      } else {
        senderName = 'Someone';
      }
      
      // Create message with sender name
      message = `${senderName} wants to connect with you`;
      console.log('[Socket] CONNECTION_REQUEST: Created connection request message', message);
      
      // Ensure we attach sender info to the notification object
      if (!data.sender) {
        data.sender = {
          id: senderId,
          name: senderName
        };
      }
      
      // Also ensure data.senderName is set for consistent access
      if (data.data && !data.data.senderName) {
        data.data.senderName = senderName;
      }
    }
    // First priority: explicit message in the notification root
    else if (data.message && typeof data.message === 'string' && data.message.trim().length > 0) {
      message = data.message.trim();
      console.log('[Socket] PRIORITY 1: Using message directly from root', message);
    } 
    // Second priority: message in the data object
    else if (data.data && data.data.message && 
             typeof data.data.message === 'string' && data.data.message.trim().length > 0) {
      message = data.data.message.trim();
      console.log('[Socket] PRIORITY 2: Using message from data object', message);
    }
    // Third priority: construct from event title for calendar requests
    else if ((data.type === 'CALENDAR_SHARE_REQUEST' || 
             data.type === 'calendar_share_request' ||
             data.type === 'EVENT_SHARE_REQUEST' ||
             data.type === 'event_share_request' ||
             (data.type && (data.type.toUpperCase().includes('CALENDAR') || 
                           data.type.toUpperCase().includes('EVENT')))) && 
             data.data) {
      const eventTitle = data.data.eventTitle || 
                         (data.data.event && data.data.event.title) ||
                         'the event';
      message = `You have been invited to "${eventTitle}"`;
      console.log('[Socket] PRIORITY 3: Constructed calendar/event share message', message);
    }
    
    // Final fallback if all else fails
    if (!message) {
      message = 'You have a new notification';
      console.log('[Socket] FALLBACK: Using default message');
    }
    
    // Build a complete notification object with all required fields
    const notification = {
      id: data.id,
      type: (data.type || 'NOTIFICATION').toUpperCase(),
      title: data.type === 'CONNECTION_REQUEST' ? 'New Connection Request' : (data.title || 'New Notification'),
      message: message, // Use our prioritized message
      timestamp: data.timestamp || new Date().toISOString(),
      data: data.data || {},
      read: false,
      sender: data.sender || null
    };
    
    // Log the complete notification for debugging
    console.log('[Socket] Formatted notification for store:', notification);
    
    // Get the Redux store reference
    const currentStore = window.store || store;
    if (!currentStore) {
      console.error('[Socket] Redux store not available for notification dispatch');
      return;
    }
    
    // Dispatch to Redux
    try {
      console.log('[Socket] Dispatching notification to Redux store');
      
      // Use specialized reducer for connection requests
      if (notification.type === 'CONNECTION_REQUEST') {
        console.log('[Socket] Using specialized CONNECTION_REQUEST reducer');
        currentStore.dispatch(addConnectionRequestNotification(data));
      } else {
        // Use general notification reducer for other types
        currentStore.dispatch(addNotification(notification));
      }
      
      // Also dispatch to window event for components to listen to
      window.dispatchEvent(new CustomEvent('notification-received', {
        detail: notification
      }));
      
      // Dispatch count update event
      window.dispatchEvent(new CustomEvent('notification-count-updated', {
        detail: { count: 1 }
      }));
      
      // If this is a calendar share request, dispatch a special event
      if (notification.type === 'CALENDAR_SHARE_REQUEST') {
        console.log('[Socket] Dispatching calendar share request event');
        window.dispatchEvent(new CustomEvent('calendar-invitation-received', {
          detail: notification
        }));
      }
      
      // If we have the NotificationAPI available, try to show a browser notification
      if ('Notification' in window && Notification.permission === 'granted') {
        try {
          const notificationOptions = {
            body: notification.message,
            icon: '/logo.jpg',
            tag: `jingleez-notification-${notification.id}`,
            data: { url: window.location.origin }
          };
          
          const browserNotification = new Notification(notification.title, notificationOptions);
          browserNotification.onclick = function() {
            window.focus();
            browserNotification.close();
          };
        } catch (notificationError) {
          console.error('[Socket] Error showing browser notification:', notificationError);
        }
      }
      
    } catch (storeError) {
      console.error('[Socket] Error dispatching to store:', storeError);
    }
  } catch (error) {
    console.error('[Socket] Error processing notification:', error);
  }
}

/**
 * Handle calendar share request
 * @param {Object} data - Calendar share request data
 * @param {Object} store - Redux store
 */
function handleCalendarShareRequest(data, store) {
  console.log('[Socket] Received calendar share request:', data);
  
  // Create notification object
  const notification = {
    id: data.id || `calendar-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`,
    type: 'CALENDAR_SHARE_REQUEST',
    title: data.title || 'New Calendar Invitation',
    message: data.message || `You've been invited to an event`,
    timestamp: data.timestamp || new Date().toISOString(),
    data: data,
    read: false
  };
  
  // Dispatch to Redux
  if (store) {
    store.dispatch(addNotification(notification));
  }
  
  // Dispatch global event
  window.dispatchEvent(new CustomEvent('notification-received', {
    detail: notification
  }));
}

/**
 * Set up client-side event listeners for cross-component communication
 */
function setupClientEventListeners() {
  // No need to set up window event listeners if we're using Redux
  // But we could add them here if needed for components that don't use Redux
}

/**
 * Fetch notifications from the server
 * @returns {Promise} - Promise that resolves when notifications are fetched
 */
export const refreshNotifications = () => {
  if (!NOTIFICATIONS_ENABLED) {
    console.log('[Socket] Notifications are disabled, skipping fetch');
    return Promise.reject('Notifications are disabled');
  }
  
  if (!window.socketInstance || window.socketInstance.readyState !== WebSocket.OPEN) {
    console.warn('[Socket] Cannot refresh notifications - socket not connected');
    return Promise.reject('Socket not connected');
  }
  
  console.log('[Socket] Fetching notifications from server');
  
  return new Promise((resolve, reject) => {
    try {
      const userId = window.store?.getState()?.auth?.user?.id;
      if (!userId) {
        return reject('No user ID available');
      }
      
      // Create a unique request ID to track response
      const requestId = `fetch-${Date.now()}`;
      
      // Send request to server
      window.socketInstance.send(JSON.stringify({
        action: 'fetch_notifications',
        userId: userId,
        requestId: requestId
      }));
      
      // We can't wait for a response in this implementation, so we resolve immediately
      // The response will be handled by the message handler
      resolve({ status: 'requested' });
    } catch (error) {
      console.error('[Socket] Error sending fetch request:', error);
      reject(error);
    }
  });
};

/**
 * Check if the socket is connected
 * @returns {boolean} - True if socket is connected
 */
export const checkSocketConnected = () => {
  return window.socketInstance && window.socketInstance.readyState === WebSocket.OPEN;
};

// Create mock functions for the connection-related APIs that were removed
// but are still imported by other components.

// Mock connection event handlers
const noop = () => {}; // No-operation function

/**
 * Get socket connection status
 * @returns {Object} Connection status object
 */
export const getSocketStatus = () => {
  return {
    connected: window.socketInstance && window.socketInstance.readyState === WebSocket.OPEN
  };
};

// Connection request event registration (mock implementations)
export const onConnectionRequest = noop;
export const onConnectionAccepted = noop;
export const onConnectionRejected = noop;
export const onConnectionRemoved = noop;

// Connection request event deregistration (mock implementations)
export const offConnectionRequest = noop;
export const offConnectionAccepted = noop;
export const offConnectionRejected = noop;
export const offConnectionRemoved = noop;

// Export the socket setup function as the default export
export default setupSockets; 